// --------------------------------------------------------------
// Created On: 2025-03-18
// Author: Dimitra Weinstein
//
// Last Modified: 2025-04-08
// Modified By: Dimitra Weinstein
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";

// A form to collect project details for a Memphis report.
export default function ProjectDetailsForm(props: Props): Component {
  return (
    <div className="mx-1">
      {/* Project Owner */}
      <label className="mb-1">Owner</label>
      <input
        data-test="report-modal-owner-input"
        className="form-control mx-auto mb-3"
        type="text"
        maxLength={50}
        value={props.owner}
        disabled={props.disabled}
        onChange={(e) => props.onOwnerChange(e.target.value)}
      />

      {/* Project Address */}
      <label className="mb-1">Address</label>
      <input
        data-test="report-modal-address-input"
        className="form-control mx-auto mb-3"
        type="text"
        maxLength={50}
        value={props.address}
        disabled={props.disabled}
        onChange={(e) => props.onAddressChange(e.target.value)}
      />

      {/* Sewer Basin */}
      <label className="mb-1">Sewer Basin</label>
      <input
        data-test="report-modal-sewer-basin-input"
        className="form-control mx-auto mb-3"
        type="text"
        maxLength={50}
        value={props.sewerBasin}
        disabled={props.disabled}
        onChange={(e) => props.onSewerBasinChange(e.target.value)}
      />

      {/* Project Inspector Name */}
      <label className="mb-1">Inspector Name</label>
      <input
        data-test="report-modal-inspector-name-input"
        className="form-control mx-auto mb-3"
        type="text"
        maxLength={50}
        value={props.inspectorName}
        disabled={props.disabled}
        onChange={(e) => props.onInspectorNameChange(e.target.value)}
      />

      {/* Pump Horsepower */}
      <label className="mb-1">Pump Horsepower (HP)</label>
      <input
        data-test="report-modal-pump-horsepower-input"
        className="form-control mx-auto mb-3"
        type="text"
        placeholder="0.0"
        maxLength={10}
        value={props.pumpHorsepower}
        disabled={props.disabled}
        onChange={(e) => props.onPumpHorsepowerChange(e.target.value)}
      />

      {/* Force Main */}
      <label className="mb-1">Force Main (In)</label>
      <input
        data-test="report-modal-force-main-input"
        className="form-control mx-auto mb-3"
        type="text"
        placeholder="0.0"
        maxLength={10}
        value={props.forceMain}
        disabled={props.disabled}
        onChange={(e) => props.onForceMainChange(e.target.value)}
      />

      {/* Approved Maximum Discharge */}
      <label className="mb-1">Approved Maximum Discharge (gpm)</label>
      <input
        data-test="report-modal-approved-max-discharge-input"
        className="form-control mx-auto mb-3"
        type="text"
        placeholder="0.0"
        maxLength={10}
        value={props.maximumDischarge}
        disabled={props.disabled}
        onChange={(e) => props.onMaximumDischargeChange(e.target.value)}
      />

      {/* Approved Pump Run Time */}
      <label className="mb-1">Approved Pump Run Time</label>
      <input
        data-test="report-modal-approved-pump-run-time-input"
        className="form-control mx-auto mb-3"
        type="text"
        placeholder="09:00 AM - 09:30 AM"
        maxLength={50}
        value={props.approvedPumpRuntime}
        disabled={props.disabled}
        onChange={(e) => props.onApprovedPumpRuntimeChange(e.target.value)}
      />

      {/* Storage Volume of Tank */}
      <label className="mb-1">Storage Volume of Tank (Gallons)</label>
      <input
        data-test="report-modal-storage-volume-of-tank-input"
        className="form-control mx-auto mb-3"
        type="text"
        placeholder="0.0"
        maxLength={10}
        value={props.storageVolumeOfTank}
        disabled={props.disabled}
        onChange={(e) => props.onStorageVolumeOfTankChange(e.target.value)}
      />

      {/* Pump Voltage */}
      <label className="mb-1">Pump Voltage (V)</label>
      <input
        data-test="report-modal-pump-voltage-input"
        className="form-control mx-auto mb-3"
        type="text"
        placeholder="0.0"
        maxLength={10}
        value={props.pumpVoltage}
        disabled={props.disabled}
        onChange={(e) => {
          props.onPumpVoltageChange(e.target.value);
        }}
      />
    </div>
  );
}

ProjectDetailsForm.propTypes = {
  disabled: PropTypes.bool.isRequired,
  reportMemphisId: PropTypes.number.isRequired,
  assetId: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  owner: PropTypes.string,
  address: PropTypes.string,
  sewerBasin: PropTypes.string,
  inspectorName: PropTypes.string,
  capacityOfPrimaryPump: PropTypes.string,
  capacityOfSecondaryPump: PropTypes.string,
  pumpHorsepower: PropTypes.string,
  forceMain: PropTypes.string,
  maximumDischarge: PropTypes.string,
  approvedPumpRuntime: PropTypes.string,
  storageVolumeOfTank: PropTypes.string,
  pumpVoltage: PropTypes.string,
  emailAddresses: PropTypes.array,
  onOwnerChange: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  onSewerBasinChange: PropTypes.func.isRequired,
  onInspectorNameChange: PropTypes.func.isRequired,
  onCapacityOfPrimaryPumpChange: PropTypes.func,
  onCapacityOfSecondaryPumpChange: PropTypes.func,
  onPumpHorsepowerChange: PropTypes.func.isRequired,
  onForceMainChange: PropTypes.func.isRequired,
  onMaximumDischargeChange: PropTypes.func.isRequired,
  onApprovedPumpRuntimeChange: PropTypes.func.isRequired,
  onStorageVolumeOfTankChange: PropTypes.func.isRequired,
  onPumpVoltageChange: PropTypes.func.isRequired,
};

interface Props {
  disabled: boolean;
  reportMemphisId: number;
  assetId: number;
  name: string;
  owner: string;
  address: string;
  sewerBasin: string;
  inspectorName: string;
  capacityOfPrimaryPump: string;
  capacityOfSecondaryPump: string;
  pumpHorsepower: string;
  forceMain: string;
  maximumDischarge: string;
  approvedPumpRuntime: string;
  storageVolumeOfTank: string;
  pumpVoltage: string;
  emailAddresses: FormEmailAddress[];
  onOwnerChange: (owner: string) => void;
  onAddressChange: (address: string) => void;
  onSewerBasinChange: (sewerBasin: string) => void;
  onInspectorNameChange: (inspectorName: string) => void;
  onCapacityOfPrimaryPumpChange: (capacity: string) => void;
  onCapacityOfSecondaryPumpChange: (capacity: string) => void;
  onPumpHorsepowerChange: (horsepower: string) => void;
  onForceMainChange: (forceMain: string) => void;
  onMaximumDischargeChange: (discharge: string) => void;
  onApprovedPumpRuntimeChange: (runtime: string) => void;
  onStorageVolumeOfTankChange: (volume: string) => void;
  onPumpVoltageChange: (voltage: string) => void;
}

interface FormEmailAddress {
  formId: number;
  value: string;
}
